import type { ForteXApi, MasterDataModel, ObjectLayerInfo } from "@fortex/api";
import { ForteXApiError, isAbortError } from "@fortex/api";
import { catalogueIconUrl, catalogueTileUrl, normaliseCatalogue } from "./normalise";
import type { Catalogue } from "./types";

export type LoadCatalogueOptions = {
  langId: number;
  includeHidden?: boolean;
  signal?: AbortSignal;
  optionalTimeoutMs?: number;
  onComplete?: (catalogue: Catalogue) => void;
};

export const defaultOptionalTimeoutMs = 20000;

export function withTimeout(signal: AbortSignal | undefined, ms: number): AbortSignal {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(new DOMException("timeout", "TimeoutError")), ms);
  const stop = () => clearTimeout(timer);

  if (signal) {
    if (signal.aborted) controller.abort(signal.reason);
    else signal.addEventListener("abort", () => controller.abort(signal.reason), { once: true });
  }

  controller.signal.addEventListener("abort", stop, { once: true });

  return controller.signal;
}

type Optional<T> = { value: T | null; timedOut: boolean };

async function optional<T>(promise: Promise<T>, parent: AbortSignal | undefined): Promise<Optional<T>> {
  try {
    return { value: await promise, timedOut: false };
  } catch (error) {
    if (parent?.aborted) throw error;
    if (isAbortError(error)) return { value: null, timedOut: true };
    if (error instanceof ForteXApiError && (error.status === 401 || error.status === 403 || error.status === 404)) return { value: null, timedOut: false };

    throw error;
  }
}

export async function loadCatalogue(api: ForteXApi, options: LoadCatalogueOptions): Promise<Catalogue> {
  const timeoutMs = options.optionalTimeoutMs ?? defaultOptionalTimeoutMs;
  const [maps, layers, objectLayers] = await Promise.all([
    api.tree("MAP", options.signal) as Promise<MasterDataModel[]>,
    api.tree("LAYER", options.signal) as Promise<MasterDataModel[]>,
    optional<ObjectLayerInfo[]>(api.layers(withTimeout(options.signal, timeoutMs)), options.signal)
  ]);

  const normalise = (objects: ObjectLayerInfo[] | null): Catalogue =>
    normaliseCatalogue({
      langId: options.langId,
      maps,
      layers,
      objectLayers: objects,
      iconUrl: catalogueIconUrl(api),
      tileUrlTemplate: catalogueTileUrl(api),
      includeHidden: options.includeHidden ?? false,
      pruneEmptyGroups: !(options.includeHidden ?? false)
    });

  if (options.onComplete && objectLayers.timedOut) {
    const onComplete = options.onComplete;

    void optional<ObjectLayerInfo[]>(api.layers(options.signal), options.signal)
      .then(objects => {
        if (options.signal?.aborted) return;
        if (objects.value !== null) onComplete(normalise(objects.value));
      })
      .catch(() => undefined);
  }

  return normalise(objectLayers.value);
}
